import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import Button from 'src/components/button/Button';
import ErrorState from 'src/components/error-state/ErrorState';
import Input from 'src/components/input/Input';
import Loader from 'src/components/loader/Loader';
import SecurityLevelCheckbox from 'src/components/security-level-checkbox/SecurityLevelCheckbox';
import settingsIllustration from 'src/icons/settings-illustration.svg';
import CopyIcon from 'src/icons/copy';
import API from 'src/utils/api';
import { isValidEmail, isValidName, isValidPassword } from 'src/utils/forms';
import { copyToClipboard, truncate } from 'src/utils/strings';

const Settings = () => {
    // Refs
    const fetchingProfile = useRef(false);
    // Form
    const [firstName, setFirstName] = useState();
    const [lastName, setLastName] = useState();
    const [email, setEmail] = useState();
    const [newPassword, setNewPassword] = useState();
    const [confirmPassword, setConfirmPassword] = useState();
    const [maxSecurityLevel, setMaxSecurityLevel] = useState(false);
    const [apiKey, setApiKey] = useState();
    const [formErrors, setErrors] = useState();
    // UI State
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [isMaxSecurityLevelModalOpen, setIsMaxSecurityLevelModalOpen] = useState(false);

    useEffect(() => {
        if (fetchingProfile.current) return; // make sure to fetch data only at first render
        const fetchBalances = async () => {
            fetchingProfile.current = true;
            try {
                const response = await API.get('/user/profile');

                setFirstName(response.data.user.firstName);
                setLastName(response.data.user.lastName);
                setEmail(response.data.user.email);
                setMaxSecurityLevel(response.data.user.securityLevel === 1);
                setApiKey(response.data.user.apiKey);

                fetchingProfile.current = false;
            } catch (error) {
                setError(true);
                toast.error('Failed to fetch profile');
                fetchingProfile.current = false;
            }
        };

        fetchBalances();
    }, []);

    const checkErrors = useCallback(() => {
        let newErrors = {};
        if (email && !isValidEmail(email)) newErrors.email = 'Please enter a valid email';
        if (newPassword && !isValidPassword(newPassword)) {
            newErrors.newPassword = 'Please enter a valid password (6 characters long)';
        }
        if (confirmPassword && newPassword && confirmPassword !== newPassword) {
            newErrors.confirmPassword = 'This field must match the new password field';
        }
        if (firstName && !isValidName(firstName)) newErrors.firstName = 'Please enter your first name';
        if (lastName && !isValidName(lastName)) newErrors.lastName = 'Please enter your last name';
        return newErrors;
    }, [email, newPassword, confirmPassword, firstName, lastName]);

    useEffect(() => {
        if (
            email === undefined ||
            newPassword === undefined ||
            confirmPassword === undefined ||
            firstName === undefined ||
            lastName === undefined
        ) {
            return;
        }
        setErrors(checkErrors());
    }, [email, firstName, lastName, checkErrors, newPassword, confirmPassword]);

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            const newErrors = checkErrors();
            if (Object.keys(newErrors).length > 0) return setErrors(newErrors);
            setLoading(true);
            await API.put('/user/profile/edit', {
                email,
                firstName,
                lastName,
                password: newPassword && confirmPassword && newPassword === confirmPassword ? newPassword : undefined,
                securityLevel: maxSecurityLevel ? 1 : 2,
            });
            window.location.href = '/';
            toast.success('Profile updated successfully');
        } catch (error) {
            setError(true);
            setLoading(false);
            toast.error('Failed to save profile');
        }
    };

    const onCopyApiKeyClick = () => {
        copyToClipboard(apiKey);
    };

    if (loading) {
        return (
            <div className="h-screen w-screen flex justify-center items-center">
                <Loader />
            </div>
        );
    }

    if (error) {
        return <ErrorState />;
    }

    return (
        <div className="pt-24 pb-12 flex justify-around items-start max-w-screen-2xl mx-auto xl:px-24">
            <div className="w-full px-8 lg:px-0 lg:w-5/12 xl:w-1/3">
                <span className="font-bold text-3xl">Settings</span>
                {apiKey && (
                    <button className="flex items-center mt-6" onClick={onCopyApiKeyClick}>
                        <span className="underline mr-2">Your API Key:</span> {truncate(apiKey, 20)}{' '}
                        <CopyIcon className="ml-2" />
                    </button>
                )}
                <form className="mt-6" onSubmit={onSubmit}>
                    <span className="font-bold text-2xl block">Profile</span>
                    <span className="text-gray-darker mb-6 block">
                        Fill in the fields that you want to update and leave the rest empty
                    </span>
                    <div className="flex w-full">
                        <Input
                            type="text"
                            value={firstName}
                            setValue={setFirstName}
                            error={formErrors && formErrors['firstName']}
                            label="First Name"
                            fieldName="firstName"
                            className="w-1/2 pr-4"
                            required={false}
                        />
                        <Input
                            type="text"
                            value={lastName}
                            setValue={setLastName}
                            error={formErrors && formErrors['lastName']}
                            label="Last Name"
                            fieldName="lastName"
                            className="w-1/2 pl-4"
                            required={false}
                        />
                    </div>
                    <Input
                        type="email"
                        value={email}
                        setValue={setEmail}
                        error={formErrors && formErrors['email']}
                        label="Email"
                        fieldName="email"
                        required={false}
                    />
                    <Input
                        type="password"
                        value={newPassword}
                        setValue={setNewPassword}
                        error={formErrors && formErrors['newPassword']}
                        label="New password"
                        fieldName="password"
                        required={false}
                    />
                    <Input
                        type="password"
                        value={confirmPassword}
                        setValue={setConfirmPassword}
                        error={formErrors && formErrors['confirmPassword']}
                        label="Confirm new password"
                        fieldName="confirmPassword"
                        required={false}
                    />
                    <SecurityLevelCheckbox
                        checked={maxSecurityLevel}
                        setChecked={setMaxSecurityLevel}
                        isModalOpen={isMaxSecurityLevelModalOpen}
                        setIsModalOpen={setIsMaxSecurityLevelModalOpen}
                    />
                    <Button primary text="Save" className="w-52" />
                </form>
            </div>
            <img
                src={settingsIllustration}
                alt="settings illustration"
                className="mt-24 hidden lg:block lg:w-5/12 xl:w-1/3 p-8"
            />
        </div>
    );
};

export default Settings;
